// ProjectSelector.jsx
// TrapperKeeper portal landing page - tk.tripleco.com
// Shows all active projects
// Admin users can create new projects inline via wizard

import { useState, useEffect, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import Footer from "./Footer";

const C = {
  dark: "#1a1a2e", gold: "#c9a84c", white: "#ffffff",
  border: "#e5e7eb", muted: "#6b7280", text: "#374151",
  green: "#27ae60", orange: "#e67e22", red: "#c0392b",
  blue: "#2980b9", light: "#f8f7f4",
};

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

export const ALL_TABS = [
  { name: "Status",                      category: "core",    desc: "Live status tracker" },
  { name: "AI Summit",                   category: "summit",  desc: "Summit agenda overview" },
  { name: "AI Charter",                  category: "docs",    desc: "AI program charter" },
  { name: "AI Business Case",            category: "docs",    desc: "Business case and ROI" },
  { name: "Data Audit",                  category: "data",    desc: "Data source audit" },
  { name: "Architecture",                category: "data",    desc: "Technical architecture" },
  { name: "AI Strategy",                 category: "docs",    desc: "AI strategy framework" },
  { name: "Agentic Day Planner",         category: "pilot",   desc: "Pilot program tracker" },
  { name: "Ownership",                   category: "docs",    desc: "Ownership matrix" },
  { name: "Metrics",                     category: "data",    desc: "KPI definitions" },
  { name: "Readiness Capture",           category: "capture", desc: "AI readiness capture" },
  { name: "Use Case Capture",            category: "capture", desc: "Use case prioritization" },
  { name: "Current State Capture",       category: "capture", desc: "Current state capture" },
  { name: "Data Platform Capture",       category: "capture", desc: "Data platform decisions" },
  { name: "Pilot Planning Capture",      category: "capture", desc: "Pilot planning" },
  { name: "Governance Capture",          category: "capture", desc: "Governance framework" },
  { name: "Story Capture",               category: "capture", desc: "Story framework" },
  { name: "Execution Capture",           category: "capture", desc: "Execution planning" },
  { name: "Day 1 Wrap Capture",          category: "capture", desc: "Day 1 decisions" },
  { name: "Summit Close Capture",        category: "capture", desc: "Final commitments" },
  { name: "New Hire Onboarding Tracker", category: "hr",      desc: "Onboarding tracker" },
  { name: "SMS Notifications",           category: "core",    desc: "Twilio SMS consent capture (compliance-required)" },
  { name: "Admin",                       category: "admin",   desc: "Platform admin" },
];

// Tabs pre-checked for every new project created via the wizard below.
// This file's ALL_TABS has no per-tab defaultOn field at all -- this array
// is the only mechanism controlling wizard defaults here. SMS Notifications
// must always be included: it is Twilio A2P 10DLC compliance-required,
// never something a project owner should have to remember to opt into.
export const DEFAULT_ENABLED_TABS = ["Status", "SMS Notifications", "Admin"];

const PERMISSION_LEVELS = ["view", "write", "modify", "admin"];
const LEVEL_COLORS = { view: C.muted, write: C.blue, modify: C.orange, admin: C.red };

const WIZARD_STEPS = [
  { id: "basics",     label: "Project",    icon: "📋" },
  { id: "sharepoint", label: "SharePoint", icon: "📊" },
  { id: "team",       label: "Team",       icon: "👥" },
  { id: "tabs",       label: "Tabs",       icon: "🗂" },
  { id: "review",     label: "Review",     icon: "✅" },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.dark, marginBottom: 3 }}>{label}</div>
      {hint && <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{hint}</div>}
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder }) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder}
      style={{ width: "100%", padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
  );
}

function StepIndicator({ currentStep }) {
  const currentIdx = WIZARD_STEPS.findIndex(function(s) { return s.id === currentStep; });
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
      {WIZARD_STEPS.map(function(step, i) {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={step.id} style={{ display: "flex", alignItems: "center", flex: i < WIZARD_STEPS.length - 1 ? 1 : 0 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: done ? C.green : active ? C.dark : "#f3f4f6", color: done ? C.white : active ? C.gold : C.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: done ? 14 : 16, fontWeight: 700, border: active ? "2px solid " + C.gold : "none" }}>
                {done ? "✓" : step.icon}
              </div>
              <div style={{ fontSize: 10, color: active ? C.dark : C.muted, fontWeight: active ? 700 : 400, whiteSpace: "nowrap" }}>{step.label}</div>
            </div>
            {i < WIZARD_STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? C.green : C.border, margin: "0 6px", marginBottom: 16 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── WIZARD STEP COMPONENTS ──────────────────────────────────────────────────

function StepBasics({ data, setData }) {
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.dark, marginBottom: 4 }}>Project Details</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Basic information about the project and client.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Project Name *"><Input value={data.name} onChange={function(e) { setData({ ...data, name: e.target.value }); }} placeholder="e.g. ACME Data Platform" /></Field>
        <Field label="Client *"><Input value={data.client} onChange={function(e) { setData({ ...data, client: e.target.value }); }} placeholder="e.g. ACME Corp" /></Field>
        <Field label="Subtitle" hint="Phase, timeline, or description"><Input value={data.subtitle} onChange={function(e) { setData({ ...data, subtitle: e.target.value }); }} placeholder="e.g. Phase 1 - AI Foundation" /></Field>
        <Field label="External URL" hint="Only set this after DNS is configured for a dedicated subdomain">
          <Input value={data.domain} onChange={function(e) {
            const val = e.target.value;
            // Reject tk.tripleco.com paths - those are auto-generated
            if (val.includes("tk.tripleco.com")) { return; }
            setData({ ...data, domain: val });
          }} placeholder="e.g. https://acme.tripleco.com (optional)" />
          <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
            Leave blank. Every project is automatically accessible at tk.tripleco.com/[project-id].
            Only set this when you have a dedicated subdomain like acme.tripleco.com configured in DNS.
          </div>
        </Field>
      </div>
    </div>
  );
}

function StepSharePoint({ data, setData, accessToken }) {
  const [searching, setSearching] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileFilter, setFileFilter] = useState("");
  const [searchError, setSearchError] = useState(null);
  const [searched, setSearched] = useState(false);

  async function findFiles() {
    if (!data.spTenant || !data.spSite) { setSearchError("Enter tenant and site name first."); return; }
    setSearching(true); setSearchError(null); setFiles([]);
    try {
      const siteRes = await fetch(GRAPH_BASE + "/sites/" + data.spTenant + ":/sites/" + data.spSite, { headers: { Authorization: "Bearer " + accessToken } });
      if (!siteRes.ok) throw new Error("Site not found. Check tenant and site name.");
      const siteData = await siteRes.json();
      const drivesRes = await fetch(GRAPH_BASE + "/sites/" + siteData.id + "/drives", { headers: { Authorization: "Bearer " + accessToken } });
      const drivesData = await drivesRes.json();
      const allFiles = [];
      for (const drive of (drivesData.value || [])) {
        try {
          const searchRes = await fetch(GRAPH_BASE + "/drives/" + drive.id + "/root/search(q='.xlsx')?$select=id,name,parentReference,lastModifiedDateTime&$top=100", { headers: { Authorization: "Bearer " + accessToken } });
          const searchData = await searchRes.json();
          for (const file of (searchData.value || [])) { allFiles.push({ ...file, driveId: drive.id, driveName: drive.name }); }
        } catch (e) { }
      }
      setFiles(allFiles); setSearched(true);
      if (allFiles.length === 0) setSearchError("No .xlsx files found.");
    } catch (e) { setSearchError(e.message); }
    finally { setSearching(false); }
  }

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.dark, marginBottom: 4 }}>SharePoint Connection</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Connect to the project status file. Enter the site details, then find your file.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <Field label="SharePoint Tenant"><Input value={data.spTenant} onChange={function(e) { setData({ ...data, spTenant: e.target.value }); }} placeholder="triplecc.sharepoint.com" /></Field>
        <Field label="Site Name"><Input value={data.spSite} onChange={function(e) { setData({ ...data, spSite: e.target.value }); }} placeholder="e.g. OperationsITHub" /></Field>
      </div>
      <button onClick={findFiles} disabled={searching || !data.spTenant || !data.spSite}
        style={{ padding: "9px 20px", background: searching ? "#9ca3af" : C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: searching ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
        {searching ? "Searching..." : "🔍 Find Excel Files"}
      </button>
      {searchError && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: C.red }}>{searchError}</div>}
      {data.spFileName && <div style={{ background: "#d1fae5", border: "1px solid #6ee7b7", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#065f46", fontWeight: 600 }}>✓ {data.spFileName}</div>}
      {files.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>{files.length} files found - select your status file</div>
          <input placeholder={"Filter " + files.length + " files..."}
            onChange={function(e) { setFileFilter(e.target.value.toLowerCase()); }}
            style={{ width: "100%", padding: "7px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 12, marginBottom: 8, boxSizing: "border-box" }} />
          {files.filter(function(f) {
            return !fileFilter || f.name.toLowerCase().includes(fileFilter) || (f.parentReference?.path || "").toLowerCase().includes(fileFilter);
          }).map(function(file) {
            const selected = data.spFileId === file.id;
            const folder = file.parentReference?.path ? (file.parentReference.path.split("root:")[1] || "/") : "/";
            return (
              <div key={file.id} onClick={function() { setData({ ...data, spDriveId: file.driveId, spFileId: file.id, spFileName: file.name }); }}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", border: "1px solid " + (selected ? C.green : C.border), borderRadius: 6, marginBottom: 6, cursor: "pointer", background: selected ? "#f0fdf4" : C.white }}>
                <span style={{ fontSize: 18 }}>📊</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: selected ? 700 : 400 }}>{file.name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{file.driveName}</div>
                </div>
                {selected && <span style={{ color: C.green, fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
      {searched && files.length === 0 && !searchError && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
          <Field label="Drive ID"><Input value={data.spDriveId} onChange={function(e) { setData({ ...data, spDriveId: e.target.value }); }} placeholder="b!lQhae..." /></Field>
          <Field label="File ID"><Input value={data.spFileId} onChange={function(e) { setData({ ...data, spFileId: e.target.value }); }} placeholder="0137WX5P..." /></Field>
        </div>
      )}
    </div>
  );
}

function StepTeam({ data, setData }) {
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.dark, marginBottom: 4 }}>Team Members</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Add team members and set their permission level.</div>
      <div style={{ background: "#f8f7f4", border: "1px solid " + C.border, borderRadius: 6, padding: "10px 14px", marginBottom: 14, fontSize: 11, color: C.muted }}>
        <strong style={{ color: C.muted }}>view</strong> (read only) · <strong style={{ color: C.blue }}>write</strong> (capture) · <strong style={{ color: C.orange }}>modify</strong> (edit) · <strong style={{ color: C.red }}>admin</strong> (full)
      </div>
      {data.team.map(function(member, i) {
        return (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 120px 32px", gap: 8, marginBottom: 8, alignItems: "center" }}>
            <input value={member.name} onChange={function(e) { const t = data.team.map(function(m, idx) { return idx === i ? { ...m, name: e.target.value } : m; }); setData({ ...data, team: t }); }}
              placeholder="Full name" style={{ padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 13 }} />
            <input value={member.email} onChange={function(e) { const t = data.team.map(function(m, idx) { return idx === i ? { ...m, email: e.target.value } : m; }); setData({ ...data, team: t }); }}
              placeholder="email@domain.com" style={{ padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 13 }} />
            <select value={member.level} onChange={function(e) { const t = data.team.map(function(m, idx) { return idx === i ? { ...m, level: e.target.value } : m; }); setData({ ...data, team: t }); }}
              style={{ padding: "8px 10px", border: "1px solid " + C.border, borderRadius: 6, fontSize: 12, color: LEVEL_COLORS[member.level] || C.dark, fontWeight: 600 }}>
              {PERMISSION_LEVELS.map(function(l) { return <option key={l} value={l}>{l}</option>; })}
            </select>
            <button onClick={function() { setData({ ...data, team: data.team.filter(function(_, idx) { return idx !== i; }) }); }}
              style={{ border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: C.red, cursor: "pointer", fontSize: 14, padding: "8px" }}>✕</button>
          </div>
        );
      })}
      <button onClick={function() { setData({ ...data, team: [...data.team, { name: "", email: "", level: "write" }] }); }}
        style={{ padding: "8px 16px", border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: C.dark, cursor: "pointer", fontSize: 12, marginTop: 4 }}>
        + Add team member
      </button>
    </div>
  );
}

function StepTabs({ data, setData }) {
  const categories = [...new Set(ALL_TABS.map(function(t) { return t.category; }))];
  const catLabels = { core: "Core", summit: "Summit", docs: "Documents", data: "Data", capture: "Capture Tabs", pilot: "Pilot", hr: "HR", admin: "Admin" };

  function toggleTab(name) {
    const enabled = data.enabledTabs.includes(name);
    setData({ ...data, enabledTabs: enabled ? data.enabledTabs.filter(function(t) { return t !== name; }) : [...data.enabledTabs, name] });
  }

  function toggleCategory(cat) {
    const catTabs = ALL_TABS.filter(function(t) { return t.category === cat; }).map(function(t) { return t.name; });
    const allEnabled = catTabs.every(function(t) { return data.enabledTabs.includes(t); });
    setData({ ...data, enabledTabs: allEnabled ? data.enabledTabs.filter(function(t) { return !catTabs.includes(t); }) : [...new Set([...data.enabledTabs, ...catTabs])] });
  }

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.dark, marginBottom: 4 }}>Enabled Tabs</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>{data.enabledTabs.length} of {ALL_TABS.length} tabs enabled. Start minimal and add what you need.</div>
      {categories.map(function(cat) {
        const catTabs = ALL_TABS.filter(function(t) { return t.category === cat; });
        const allEnabled = catTabs.every(function(t) { return data.enabledTabs.includes(t.name); });
        const someEnabled = catTabs.some(function(t) { return data.enabledTabs.includes(t.name); });
        return (
          <div key={cat} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <input type="checkbox" checked={allEnabled} ref={function(el) { if (el) el.indeterminate = someEnabled && !allEnabled; }}
                onChange={function() { toggleCategory(cat); }} style={{ cursor: "pointer" }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: C.dark, textTransform: "uppercase", letterSpacing: 0.5 }}>{catLabels[cat] || cat}</span>
              <span style={{ fontSize: 11, color: C.muted }}>({catTabs.filter(function(t) { return data.enabledTabs.includes(t.name); }).length}/{catTabs.length})</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 24 }}>
              {catTabs.map(function(tab) {
                const enabled = data.enabledTabs.includes(tab.name);
                return (
                  <div key={tab.name} onClick={function() { toggleTab(tab.name); }} title={tab.desc}
                    style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid " + (enabled ? C.dark : C.border), background: enabled ? C.dark : C.white, color: enabled ? C.gold : C.muted, fontSize: 12, cursor: "pointer", fontWeight: enabled ? 600 : 400, userSelect: "none" }}>
                    {tab.name}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepReview({ data }) {
  const validTeam = data.team.filter(function(m) { return m.name && m.email; });
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.dark, marginBottom: 4 }}>Review and Create</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Confirm project details before creating.</div>
      <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 14, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 10 }}>Project</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[["Name", data.name], ["Client", data.client], ["Subtitle", data.subtitle || "—"], ["URL", data.domain || "—"]].map(function([l, v]) {
            return <div key={l}><div style={{ fontSize: 10, color: C.muted }}>{l}</div><div style={{ fontSize: 13, fontWeight: 600 }}>{v}</div></div>;
          })}
        </div>
      </div>
      <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 14, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 8 }}>SharePoint</div>
        {data.spFileName
          ? <div style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>✓ {data.spFileName}</div>
          : <div style={{ fontSize: 12, color: C.orange }}>⚠ No file selected - Status tab will not load live data</div>}
      </div>
      <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 14, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 8 }}>Team ({validTeam.length})</div>
        {validTeam.map(function(m) {
          return (
            <div key={m.email} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12 }}>
              <span>{m.name} <span style={{ color: C.muted }}>{m.email}</span></span>
              <span style={{ color: LEVEL_COLORS[m.level] || C.muted, fontWeight: 600, fontSize: 11 }}>{m.level}</span>
            </div>
          );
        })}
      </div>
      <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 8 }}>Tabs ({data.enabledTabs.length})</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {data.enabledTabs.map(function(tab) { return <span key={tab} style={{ padding: "2px 8px", borderRadius: 10, background: "#f3f4f6", color: C.muted, fontSize: 11 }}>{tab}</span>; })}
        </div>
      </div>
    </div>
  );
}

// ─── PROJECT WIZARD ───────────────────────────────────────────────────────────

function ProjectWizard({ onSave, onCancel, accessToken }) {
  const [step, setStep] = useState("basics");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    name: "", client: "", subtitle: "", domain: "",
    spTenant: "triplecc.sharepoint.com", spSite: "",
    spDriveId: "", spFileId: "", spFileName: "",
    team: [{ name: "", email: "", level: "write" }],
    enabledTabs: DEFAULT_ENABLED_TABS,
  });

  const stepIdx = WIZARD_STEPS.findIndex(function(s) { return s.id === step; });
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === WIZARD_STEPS.length - 1;
  function canAdvance() { return step !== "basics" || (data.name && data.client); }

  function next() {
    if (!canAdvance()) { setError("Project name and client are required."); return; }
    setError(null);
    setStep(WIZARD_STEPS[stepIdx + 1].id);
  }

  async function handleCreate() {
    setSaving(true); setError(null);
    try {
      const validTeam = data.team.filter(function(m) { return m.name && m.email; });
      const team = {};
      const userOverrides = {};
      validTeam.forEach(function(m) { team[m.name] = m.email; userOverrides[m.email.toLowerCase()] = m.level; });

      const payload = {
        name: data.name, client: data.client, subtitle: data.subtitle, domain: data.domain,
        sharepoint: { tenant: data.spTenant, site: data.spSite, driveId: data.spDriveId, statusFileId: data.spFileId, statusFileName: data.spFileName, summitDataFolder: "" },
        team,
        tabs: {
          public: data.enabledTabs.includes("Status") ? ["Status"] : [],
          authenticated: data.enabledTabs.filter(function(t) { return t !== "Status" && t !== "Admin"; }),
          admin: data.enabledTabs.includes("Admin") ? ["Admin"] : [],
        },
        permissions: { groups: {}, userOverrides, defaultLevel: "write" },
      };

      // Admin-only on the server; accessToken is the signed-in user's Graph token.
      const res = await fetch("/api/projects", { method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + accessToken }, body: JSON.stringify(payload) });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to create project");
      onSave(result);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "24px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onCancel} style={{ padding: "6px 12px", border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: C.muted, cursor: "pointer", fontSize: 12 }}>✕ Cancel</button>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>New Project</div>
      </div>
      <StepIndicator currentStep={step} />
      <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 20, minHeight: 300, marginBottom: 14 }}>
        {step === "basics"     && <StepBasics data={data} setData={setData} />}
        {step === "sharepoint" && <StepSharePoint data={data} setData={setData} accessToken={accessToken} />}
        {step === "team"       && <StepTeam data={data} setData={setData} />}
        {step === "tabs"       && <StepTabs data={data} setData={setData} />}
        {step === "review"     && <StepReview data={data} />}
      </div>
      {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: C.red }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={function() { setError(null); setStep(WIZARD_STEPS[stepIdx - 1].id); }} disabled={isFirst}
          style={{ padding: "9px 20px", border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: isFirst ? C.muted : C.dark, cursor: isFirst ? "not-allowed" : "pointer", fontSize: 13 }}>
          ← Back
        </button>
        <div style={{ fontSize: 11, color: C.muted }}>Step {stepIdx + 1} of {WIZARD_STEPS.length}</div>
        {isLast
          ? <button onClick={handleCreate} disabled={saving} style={{ padding: "9px 24px", background: saving ? "#9ca3af" : C.green, color: C.white, border: "none", borderRadius: 6, cursor: saving ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>{saving ? "Creating..." : "Create Project"}</button>
          : <button onClick={next} disabled={!canAdvance()} style={{ padding: "9px 20px", background: canAdvance() ? C.dark : "#9ca3af", color: C.gold, border: "none", borderRadius: 6, cursor: canAdvance() ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700 }}>Next →</button>
        }
      </div>
    </div>
  );
}

// ─── MAIN PROJECT SELECTOR ────────────────────────────────────────────────────

export default function ProjectSelector({ initialPath }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [pathProject, setPathProject] = useState(null);

  const user = accounts[0];
  const userEmail = user?.username || "";

  useEffect(function() {
    document.title = "TrapperKeeper";
  }, []);

  const loadProjects = useCallback(function() {
    setLoading(true);
    fetch("/api/projects")
      .then(function(r) { return r.json(); })
      .then(function(d) { setProjects(d.projects || []); setLoading(false); })
      .catch(function(e) { setError(e.message); setLoading(false); });
  }, []);

  useEffect(function() { loadProjects(); }, [loadProjects]);

  // Path-based project lookup
  // tk.tripleco.com/acme -> find project with id="acme" or path="/acme"
  useEffect(function() {
    if (!initialPath) return;
    const projectId = initialPath.replace(/^\/+/, "").toLowerCase();
    fetch("/api/projects/" + projectId)
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.id) setPathProject(d);
      })
      .catch(function() {});
  }, [initialPath]);

  // Admin-only on the server (a non-admin's 403 reads as "not an admin"), so
  // it waits for the token acquired below.
  useEffect(function() {
    if (!userEmail || !accessToken) return;
    fetch("/api/admin/acl?email=" + encodeURIComponent(userEmail), { headers: { Authorization: "Bearer " + accessToken } })
      .then(function(r) { return r.ok ? r.json() : { isAdmin: false }; })
      .then(function(d) { setIsAdmin(!!d.isAdmin); })
      .catch(function() { setIsAdmin(false); });
  }, [userEmail, accessToken]);

  useEffect(function() {
    if (!isAuthenticated || !accounts[0]) return;
    instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] })
      .then(function(r) { setAccessToken(r.accessToken); })
      .catch(function() {});
  }, [isAuthenticated, accounts, instance]);

  const activeProjects = projects.filter(function(p) {
    if (p.status === "archived") return false;
    if (!search) return true;
    return p.name.toLowerCase().includes(search.toLowerCase()) || p.client.toLowerCase().includes(search.toLowerCase());
  });

  // Show project detail view when accessed by path
  if (initialPath && pathProject) {
    return (
      <div style={{ minHeight: "100vh", background: C.light, fontFamily: "system-ui, -apple-system, sans-serif" }}>
        <div style={{ background: C.dark, padding: "14px 32px", display: "flex", alignItems: "center", gap: 12 }}>
          <a href="https://tk.tripleco.com" style={{ fontSize: 12, color: C.gold, textDecoration: "none", fontWeight: 700 }}>← TrapperKeeper</a>
          <span style={{ color: "#4b5563" }}>›</span>
          <div style={{ fontSize: 13, color: "#9ca3af" }}>{pathProject.name}</div>
        </div>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px" }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: C.dark, marginBottom: 4 }}>{pathProject.name}</div>
            <div style={{ fontSize: 14, color: C.muted }}>{pathProject.client}</div>
          </div>
          {(() => {
            const tabs = pathProject.tabs || {};
            const tabCount = pathProject.tabCount || (tabs.public || []).length + (tabs.authenticated || []).length + (tabs.admin || []).length;
            const teamCount = pathProject.teamCount || Object.keys(pathProject.team || {}).length;
            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
                <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>TABS</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.dark }}>{tabCount}</div>
                </div>
                <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>TEAM</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.dark }}>{teamCount}</div>
                </div>
                <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>STATUS</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>{pathProject.status || "active"}</div>
                </div>
              </div>
            );
          })()}
          {(() => {
            const projectUrl = (pathProject.domain && !pathProject.domain.includes("tk.tripleco.com")) ? pathProject.domain : null;
            const isExternal = !!projectUrl;
            return projectUrl ? (
              <div>
                <a href={projectUrl}
                  style={{ display: "inline-block", padding: "12px 28px", background: C.dark, color: C.gold, borderRadius: 8, textDecoration: "none", fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
                  Open {pathProject.name} →
                </a>
                {isExternal && (
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Opens at {projectUrl}</div>
                )}
              </div>
            ) : (
              <div style={{ padding: "12px 16px", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, fontSize: 13, color: "#92400e" }}>
                No URL configured yet. Set the domain in Admin to point this project to its live URL.
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Path given but project not found
  if (initialPath && !pathProject && !loading) {
    return (
      <div style={{ minHeight: "100vh", background: C.light, fontFamily: "system-ui, -apple-system, sans-serif" }}>
        <div style={{ background: C.dark, padding: "14px 32px" }}>
          <a href="https://tk.tripleco.com" style={{ fontSize: 12, color: C.gold, textDecoration: "none", fontWeight: 700 }}>← TrapperKeeper</a>
        </div>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "60px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.dark, marginBottom: 8 }}>Project not found</div>
          <div style={{ fontSize: 14, color: C.muted, marginBottom: 24 }}>No project found at {initialPath}</div>
          <a href="https://tk.tripleco.com" style={{ padding: "10px 24px", background: C.dark, color: C.gold, borderRadius: 6, textDecoration: "none", fontSize: 13, fontWeight: 700 }}>
            View all projects
          </a>
        </div>
      </div>
    );
  }

  if (showWizard) {
    return (
      <div style={{ minHeight: "100vh", background: C.light, fontFamily: "system-ui, -apple-system, sans-serif", padding: "0 24px" }}>
        <div style={{ background: C.dark, margin: "0 -24px", padding: "14px 32px", display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <a href="https://tk.tripleco.com" style={{ fontSize: 13, fontWeight: 700, color: C.gold, textDecoration: "none", cursor: "pointer" }}>TrapperKeeper</a>
          <span style={{ color: "#4b5563" }}>›</span>
          <div style={{ fontSize: 13, color: "#9ca3af" }}>New Project</div>
        </div>
        <ProjectWizard
          accessToken={accessToken}
          onSave={function(proj) { setShowWizard(false); loadProjects(); }}
          onCancel={function() { setShowWizard(false); }}
        />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.light, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ background: C.dark, padding: "0" }}>
        <div style={{ background: C.white, padding: "10px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div>
              <a href="https://tk.tripleco.com" style={{ fontSize: 13, fontWeight: 700, color: C.dark, textDecoration: "none", cursor: "pointer" }}>TrapperKeeper</a>
              <div style={{ fontSize: 10, color: C.muted, letterSpacing: 0.5 }}>Project Intelligence Platform</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <a href="/docs" style={{ fontSize: 12, color: C.muted, textDecoration: "none" }}>Docs</a>
            {isAuthenticated ? (
              <>
                <div style={{ fontSize: 12, color: C.muted }}>{user?.name || user?.username}</div>
                {isAdmin && (
                  <button onClick={function() { setShowWizard(true); }}
                    style={{ padding: "7px 16px", background: C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                    + New Project
                  </button>
                )}
                <button onClick={function() { instance.logoutPopup().catch(function(e) { if (e.errorCode !== "user_cancelled") console.error(e); }); }}
                  style={{ padding: "6px 14px", border: "1px solid " + C.border, borderRadius: 6, background: C.white, color: C.muted, cursor: "pointer", fontSize: 12 }}>
                  Sign out
                </button>
              </>
            ) : (
              <button onClick={function() { instance.loginPopup(loginRequest).catch(function(e) { if (e.errorCode !== "user_cancelled") console.error(e); }); }}
                style={{ padding: "7px 18px", background: C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
                Sign in
              </button>
            )}
          </div>
        </div>
        <div style={{ padding: "28px 32px 24px" }}>
          <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Triple Crown Consulting</div>
          <a href="https://tk.tripleco.com" style={{ display: "inline-block", fontSize: 28, fontWeight: 700, color: C.white, marginBottom: 6, textDecoration: "none", cursor: "pointer" }}>TrapperKeeper</a>
          <div style={{ fontSize: 14, color: "#9ca3af" }}>Project Intelligence Platform · {activeProjects.length} active project{activeProjects.length !== 1 ? "s" : ""}</div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px" }}>
        <div style={{ marginBottom: 24 }}>
          <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder="Search projects..."
            style={{ width: "100%", padding: "10px 16px", border: "1px solid " + C.border, borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: C.white }} />
        </div>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: C.red }}>Could not load projects: {error}</div>}
        {loading && <div style={{ textAlign: "center", padding: 60, color: C.muted }}>Loading projects...</div>}

        {!loading && activeProjects.length === 0 && !error && (
          <div style={{ textAlign: "center", padding: 60, color: C.muted }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>📁</div>
            <div>{search ? "No projects match your search." : "No active projects."}</div>
            {isAdmin && !search && (
              <button onClick={function() { setShowWizard(true); }}
                style={{ marginTop: 16, padding: "10px 24px", background: C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 700 }}>
                + Create First Project
              </button>
            )}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 16 }}>
          {activeProjects.map(function(project) {
            return (
              <div key={project.id}
                onClick={function() { window.location.href = "https://tk.tripleco.com/" + project.id; }}
                style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 10, padding: 20, cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                onMouseEnter={function(e) { if (project.domain) e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; }}
                onMouseLeave={function(e) { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green }} />
                      <div style={{ fontSize: 16, fontWeight: 700, color: C.dark }}>{project.name}</div>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted }}>{project.client}</div>
                  </div>
                  {project.domain && <div style={{ fontSize: 20, color: C.muted }}>›</div>}
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 11, color: C.muted, marginBottom: 12 }}>
                  <span>{project.tabCount || 0} tabs</span>
                  <span>{project.teamCount || 0} members</span>
                  <span>Updated {project.updatedAt || "—"}</span>
                </div>
                <div style={{ padding: "7px 12px", background: C.light, borderRadius: 6, fontSize: 11, color: C.muted, fontFamily: "monospace" }}>
                  {project.domain || "No URL configured"}
                </div>
              </div>
            );
          })}
        </div>

        <Footer />
      </div>
    </div>
  );
}
